import { Loader2, ChevronLeft } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import PhoneInput, { isPossiblePhoneNumber, type Value } from "react-phone-number-input";
import "react-phone-number-input/style.css";

import { AuthShell } from "@/components/shared/AuthShell";
import { CodeInput } from "@/components/shared/CodeInput";
import { FileUpload } from "@/components/shared/FileUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useMyProfileAvatarObjectUrl } from "@/hooks/useMyProfileAvatarObjectUrl";
import {
  requestMyPhoneCode,
  uploadProfileImage,
  verifyMyPhoneCode,
} from "@/services/authService";

/**
 * Post-payment "complete profile" gate. Runs after a successful certificate
 * request payment when the visitor is missing an avatar AND/OR a verified
 * phone. On verification the backend auto-promotes the user's
 * `pending_profile_completion` requests to `pending` (see the matching
 * promotion helpers in NotarialRequestService / AccountantRequestService),
 * then we forward to the `?next=` URL — usually the new request's detail.
 *
 * "Lo haré después" lets the visitor bail; the request stays parked and
 * the detail page surfaces a banner reminding them to come back.
 */

const RESEND_COOLDOWN_SECONDS = 60;

type Step = "collect" | "verify";

export function CompleteProfilePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isLoading, refreshUser } = useAuth();
  const queryClient = useQueryClient();


  const nextLocation = searchParams.get("next") || "/";

  /**
   * Leaves the profile flow with the rest of the app told to refetch.
   *
   * Completing a profile PROMOTES every request sitting in
   * pending_profile_completion, server-side — so the data the user is about to
   * land on is stale by definition. The query client caches for 60s
   * (App.tsx), which is longer than it takes to finish this form and press
   * back, so the request detail kept rendering "En pausa" for a request that
   * was already in progress.
   *
   * Invalidated by the "me" prefix rather than a single request id: the
   * promotion is not scoped to the request the user came from, and any number
   * of their other requests may have moved at the same moment.
   */
  const leaveProfileFlow = useCallback(async () => {
    await refreshUser();
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    navigate(nextLocation, { replace: true });
  }, [refreshUser, queryClient, navigate, nextLocation]);

  const currentAvatarUrl = useMyProfileAvatarObjectUrl(
    user?.profileImage ?? null,
  );

  const [step, setStep] = useState<Step>("collect");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [hasUploadedThisSession, setHasUploadedThisSession] = useState(false);

  const [phone, setPhone] = useState<Value | undefined>(undefined);
  const [code, setCode] = useState("");

  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Phone validity gates the "Enviar código" button. We additionally
  // require the user to have an avatar on file — uploaded this session or
  // already present from a previous one — before sending the code so the
  // backend can promote the request the moment Twilio approves.
  const isPhoneValid = useMemo(
    () => phone != null && isPossiblePhoneNumber(phone),
    [phone],
  );
  const hasAvatarOnServer =
    (user?.profileImage != null && user.profileImage.length > 0) ||
    hasUploadedThisSession;

  // Resend cooldown ticker.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const uploadAvatarIfNeeded = useCallback(async (): Promise<boolean> => {
    if (selectedFile == null) return true; // nothing to upload this session
    setIsUploadingAvatar(true);
    try {
      await uploadProfileImage(selectedFile);
      setHasUploadedThisSession(true);
      // Refresh AuthUser so downstream screens (and the gate guard) see the
      // new profileImage immediately. The backend's promotion helper also
      // fires on this endpoint — if phone was already verified previously,
      // the request is already live by the time we return here.
      await refreshUser();
      return true;
    } catch {
      toast.error(
        t(
          "auth.completeProfile.avatarError",
          "No se pudo subir la foto. Intentalo de nuevo.",
        ),
      );
      return false;
    } finally {
      setIsUploadingAvatar(false);
    }
  }, [selectedFile, refreshUser, t]);

  const handleSendCode = useCallback(
    async (channel: "sms" | "call" = "sms") => {
      if (!isPhoneValid || phone == null) {
        toast.error(
          t(
            "auth.completeProfile.invalidPhone",
            "Ingresá un teléfono válido con código de país.",
          ),
        );
        return;
      }
      // Upload the avatar first so the backend promotion can run as a
      // single transaction when verify-code completes.
      const okAvatar = await uploadAvatarIfNeeded();
      if (!okAvatar) return;

      setIsSendingCode(true);
      try {
        const result = await requestMyPhoneCode(phone, channel);
        if (result.alreadyVerified) {
          // Same number is already approved — backend already promoted any
          // parked requests on a prior verify. Just forward.
          await leaveProfileFlow();
          return;
        }
        setStep("verify");
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        toast.success(
          channel === "call"
            ? t(
                "auth.completeProfile.callSent",
                "Te llamaremos para dictarte el código.",
              )
            : t(
                "auth.completeProfile.smsSent",
                "Te enviamos un SMS con el código.",
              ),
        );
      } catch (err) {
        const msg =
          err instanceof Error
            ? err.message
            : t("auth.completeProfile.sendError", "No se pudo enviar el código.");
        toast.error(msg);
      } finally {
        setIsSendingCode(false);
      }
    },
    [phone, isPhoneValid, uploadAvatarIfNeeded, leaveProfileFlow, t],
  );

  const handleVerify = useCallback(async () => {
    if (phone == null) return;
    const trimmed = code.trim();
    if (trimmed.length < 4) return;
    setIsVerifying(true);
    try {
      await verifyMyPhoneCode(phone, trimmed);
      toast.success(
        t("auth.completeProfile.verified", "Perfil completo. ¡Listo!"),
      );
      await leaveProfileFlow();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : t(
              "auth.completeProfile.verifyError",
              "Código inválido o expirado.",
            );
      toast.error(msg);
    } finally {
      setIsVerifying(false);
    }
  }, [phone, code, leaveProfileFlow, t]);

  if (isLoading) return <></>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Already complete? Skip the gate — the wizard wouldn't have routed here,
  // but a manual nav (or a refresh after completing) should bounce.
  if (
    user != null &&
    user.profileImage != null &&
    user.profileImage.length > 0 &&
    user.phoneVerifiedAt != null
  ) {
    return <Navigate to={nextLocation} replace />;
  }

  return (
    /* Cover-less: this is a post-payment interstitial inside the product, not
       a door into it, and it carries a "lo haré después" bail. A marketing
       half opposite it would read as an ad in the middle of a checkout. */
    <AuthShell>
      <div className="space-y-6">
        {step === "collect" ? (
          <>
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">
                {t(
                  "auth.completeProfile.title",
                  "Completá tu perfil",
                )}
              </h1>
              <p className="text-balance text-muted-foreground">
                {t(
                  "auth.completeProfile.subtitle",
                  "Los profesionales prefieren tomar solicitudes de usuarios con foto y teléfono verificado. Te lleva un minuto.",
                )}
              </p>
            </div>

            <div className="space-y-2">
              <Label>
                {t("auth.completeProfile.photoLabel", "Foto de perfil")}
              </Label>
              <FileUpload
                currentImageUrl={currentAvatarUrl}
                onFileChange={setSelectedFile}
                disabled={isUploadingAvatar || isSendingCode}
                showLabel={false}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="complete-profile-phone">
                {t("auth.completeProfile.phoneLabel", "Teléfono móvil")}
                <span className="ml-1 text-destructive">*</span>
              </Label>
              {/*
                react-phone-number-input renders a country picker + local
                number input. `value` is the E.164 representation —
                exactly what the backend expects.
              */}
              <PhoneInput
                id="complete-profile-phone"
                international
                defaultCountry="UY"
                value={phone}
                onChange={setPhone}
                inputComponent={Input}
                className="rounded-md border border-input bg-background px-3 py-2"
                disabled={isSendingCode || isUploadingAvatar}
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  "auth.completeProfile.phoneHint",
                  "Recibirás un código por SMS — también podés pedir una llamada.",
                )}
              </p>
            </div>

            <Button
              type="button"
              onClick={() => void handleSendCode()}
              disabled={
                !isPhoneValid ||
                isSendingCode ||
                isUploadingAvatar ||
                (!selectedFile && !hasAvatarOnServer)
              }
              className="w-full transition-colors duration-150"
            >
              {(isSendingCode || isUploadingAvatar) ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  {t("auth.completeProfile.sending", "Enviando…")}
                </>
              ) : (
                t("auth.completeProfile.sendCode", "Enviar código")
              )}
            </Button>

            <button
              type="button"
              disabled={isSendingCode}
              onClick={() => {
                navigate(nextLocation, { replace: true });
              }}
              className="block w-full text-center text-sm text-muted-foreground underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
            >
              {t("auth.completeProfile.skip", "Lo haré después")}
            </button>
          </>
        ) : (
          <>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => {
                  setStep("collect");
                  setCode("");
                }}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                {t("auth.completeProfile.back", "Atrás")}
              </button>
              <h1 className="text-2xl font-bold text-foreground">
                {t(
                  "auth.completeProfile.verifyTitle",
                  "Verificá tu teléfono",
                )}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t(
                  "auth.completeProfile.verifyBody",
                  "Te enviamos un código a {{phone}}.",
                  { phone: phone ?? "" },
                )}
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="complete-profile-code">
                {t("auth.completeProfile.codeLabel", "Código de verificación")}
              </Label>
              {/* The same six segmented boxes as the three 2FA moments: this
                  is a six-digit code too, and the shared input is what makes
                  the submit gate exact instead of "at least four". */}
              <CodeInput
                id="complete-profile-code"
                value={code}
                onChange={setCode}
                onComplete={() => void handleVerify()}
                disabled={isVerifying}
              />
            </div>

            <Button
              type="button"
              onClick={() => void handleVerify()}
              disabled={code.length !== 6 || isVerifying}
              className="w-full transition-colors duration-150"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  {t("auth.completeProfile.verifying", "Verificando…")}
                </>
              ) : (
                t("auth.completeProfile.verify", "Verificar")
              )}
            </Button>

            <div className="flex items-center justify-center gap-3 text-sm">
              {resendCooldown > 0 ? (
                <span className="text-muted-foreground">
                  {t(
                    "auth.completeProfile.resendIn",
                    "Reenviar en {{seconds}}s",
                    { seconds: resendCooldown },
                  )}
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={isSendingCode}
                    onClick={() => void handleSendCode("sms")}
                    className="text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-50"
                  >
                    {t("auth.completeProfile.resendSms", "Reenviar SMS")}
                  </button>
                  <span className="text-muted-foreground">·</span>
                  <button
                    type="button"
                    disabled={isSendingCode}
                    onClick={() => void handleSendCode("call")}
                    className="text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-50"
                  >
                    {t("auth.completeProfile.tryCall", "Probar llamada")}
                  </button>
                </>
              )}
            </div>

          </>
        )}
      </div>
    </AuthShell>
  );
}
